/**
 * useTheme — Hook para sistema de temas do WhiteLabelClaw
 * 
 * Retorna o tema ativo e funções pra trocar.
 * Re-renderiza automaticamente quando o tema muda.
 * 
 * USO:
 *   const { theme, themeId, setTheme, allThemes } = useTheme();
 *   <div style={{ background: theme.bg, color: theme.text }}>
 */
import { useState, useEffect } from 'preact/hooks';
import {
  getActiveTheme,
  getActiveThemeId,
  getAllThemes,
  applyTheme,
  loadCustomThemes,
  registerTheme,
  createThemeFromShadcn,
  loadThemeFont,
  type ThemeColors,
} from '../styles/themes';

export function useTheme() {
  const [themeId, setThemeId] = useState(getActiveThemeId);
  const [theme, setThemeState] = useState<ThemeColors>(getActiveTheme);

  useEffect(() => {
    loadCustomThemes();
    loadThemeFont(getActiveThemeId());

    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      setThemeId(detail.themeId);
      setThemeState(detail.theme);
    };

    window.addEventListener('theme-change', handler);
    return () => window.removeEventListener('theme-change', handler);
  }, []);

  const setTheme = (id: string) => {
    applyTheme(id);
  };

  const importFromShadcn = (css: string, name: string) => {
    const id = name.toLowerCase().replace(/\s+/g, '-');
    const newTheme = createThemeFromShadcn(css, name);
    registerTheme(id, newTheme);
    applyTheme(id);
    return id;
  };

  return {
    theme,
    themeId,
    setTheme,
    importFromShadcn,
    allThemes: getAllThemes(),
  };
}
